import type { TokenPayload } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { Token } from './types.js';

const TOKEN_PARTS = 3;
const TOKEN_BODY_INDEX = 1;

const decodeBase64 = (value: string): string => {
  if (typeof globalThis.atob === 'function') {
    try {
      return globalThis.atob(value);
    } catch {
      // Falls through to Buffer-based decoding in non-browser runtimes.
    }
  }

  return Buffer.from(value, 'base64').toString('utf8');
};

const isTokenPayload = (value: unknown): value is TokenPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Partial<TokenPayload>;

  return (
    typeof payload.aud === 'string' &&
    typeof payload.exp === 'number' &&
    typeof payload.iat === 'number' &&
    typeof payload.iss === 'string' &&
    typeof payload.jti === 'string' &&
    (payload.naf === undefined || typeof payload.naf === 'number') &&
    typeof payload.nbf === 'number' &&
    (payload.oid === undefined || typeof payload.oid === 'string') &&
    (payload.patrolId === undefined || typeof payload.patrolId === 'string') &&
    typeof payload.rem === 'boolean' &&
    Array.isArray(payload.rls) &&
    payload.rls.every((role) => typeof role === 'string') &&
    typeof payload.sid === 'string' &&
    typeof payload.shr === 'boolean' &&
    typeof payload.sty === 'string' &&
    typeof payload.sub === 'string' &&
    typeof payload.typ === 'string'
  );
};

export const decodeToken = (raw: string): Token | null => {
  if (!raw) return null;
  const payload = decomposeJwt(raw);
  if (!payload) return null;
  return {
    expires: payload.exp * 1000,
    payload,
    raw,
    refreshExpires: payload.naf ? payload.naf * 1000 : undefined,
  } as Token;
};

export const decomposeJwt = (token: string): TokenPayload | null => {
  if (!token || token.split('.').length !== TOKEN_PARTS) {
    return null;
  }

  const base64Url = token.split('.')[TOKEN_BODY_INDEX];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  try {
    const decoded = JSON.parse(decodeBase64(paddedBase64)) as unknown;
    // A super admin has no organization. Tokens issued before the api stopped
    // doing so carry `oid: null`; treat that as absent rather than malformed.
    if (
      decoded &&
      typeof decoded === 'object' &&
      (decoded as { oid?: unknown }).oid === null
    ) {
      delete (decoded as { oid?: unknown }).oid;
    }
    if (!isTokenPayload(decoded)) {
      return null;
    }

    return decoded;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const isJwtExpired = (raw: string): boolean => {
  const token = decodeToken(raw);
  if (!token) return true;
  return token.expires < Date.now();
};

export const base64URLEncode = (bytes: number[]): string => {
  return Buffer.from(String.fromCharCode.apply(null, bytes))
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};
